import React, { useState, useEffect } from 'react';
import { FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLocalization } from '../../hooks/useLocalization';

interface Booking {
    _id: string;
    reservationNumber: string;
    status: string;
    totalAmount: number;
    currency: string;
    createdAt: string;
    contact: {
        firstName: string;
        lastName: string;
    };
    firstName?: string;
    lastName?: string;
    tourvisioId?: string;
}

const UserBookings: React.FC = () => {
    const [bookings, setBookings] = useState<Booking[]>([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { language } = useLocalization();
    const sv = language === 'sv';

    const fetchBookings = async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/bookings/my-bookings');
            const data = await res.json();
            if (data.success && data.bookings) {
                setBookings(data.bookings);
            }
        } catch (error) {
            console.error('Failed to fetch bookings', error);
        } finally {
            setLoading(false);
        }
    };

    const handleViewDocuments = async (bookingId: string) => {
        navigate(`/user/bookings/${bookingId}`);
    };

    const statusLabel = (status: string) => {
        if (sv) {
            if (status === 'confirmed') return 'Bekräftad';
            if (status === 'cancelled') return 'Avbokad';
            if (status === 'pending') return 'Väntar';
            return status;
        }
        return status.charAt(0).toUpperCase() + status.slice(1);
    };

    useEffect(() => {
        fetchBookings();
    }, []);

    return (
        <div className="space-y-4">
            {loading ? (
                <div className="text-center text-gray-500 py-14 flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-3 border-gray-300 border-t-teal-600 rounded-full animate-spin"></div>
                    <span className="text-sm">{sv ? 'Laddar bokningar...' : 'Loading bookings...'}</span>
                </div>
            ) : bookings.length === 0 ? (
                <div className="p-10 text-center bg-white border border-gray-200 rounded-2xl shadow-sm">
                    <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                    <p className="text-gray-500 mb-4 text-sm">{sv ? 'Du har inga bokningar ännu.' : "You haven't made any bookings yet."}</p>
                    <button
                        onClick={() => navigate('/')}
                        className="px-5 py-2.5 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700 transition-colors text-sm"
                    >
                        {sv ? 'Utforska resor' : 'Browse Tours'}
                    </button>
                </div>
            ) : (
                <div className="grid gap-4">
                    {bookings.map((booking) => (
                        <div key={booking._id} className="p-5 bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md hover:border-gray-300 transition-all">
                            <div className="flex justify-between items-start md:items-center flex-col md:flex-row gap-4">
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="text-lg font-bold text-gray-900">
                                            #{booking.reservationNumber || 'PENDING'}
                                        </h3>
                                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                                            booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                                'bg-yellow-100 text-yellow-700'
                                            }`}>
                                            {statusLabel(booking.status)}
                                        </span>
                                    </div>
                                    <p className="text-gray-500 text-sm">
                                        {new Date(booking.createdAt).toLocaleDateString(sv ? 'sv-SE' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                    </p>
                                    <p className="text-gray-600 text-sm font-medium">
                                        {booking.contact?.firstName} {booking.contact?.lastName}
                                    </p>
                                </div>

                                <div className="text-right">
                                    <div className="text-xl font-extrabold text-gray-900 mb-2">
                                        {booking.totalAmount?.toLocaleString('sv-SE')} {booking.currency || 'SEK'}
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleViewDocuments(booking.reservationNumber)}
                                            className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-200 transition-colors flex items-center gap-2"
                                        >
                                            <FileText className="w-4 h-4" /> {sv ? 'Detaljer' : 'Details'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default UserBookings;
